/**
 * SearchField component
 * @author Liwei Yeh <liwei.yeh@adasasistemas.com>
 */

// Library
import { useEffect, useState } from 'react';

// CSS Style for this module
import styles from './searchField.module.scss';

/**
 * Interface for SearchField props
 */
interface SearchFieldProps {
	/**
	 * Custom styles of the button
	 */
	customStyles?: string;
	/**
	 * label of the input element
	 */
	label?: string;
	/**
	 * Default text that is displayed within the field
	 */
	placeholder?: string;
	/**
	 * Msg to be displayed when the field is error
	 */
	errorMsg?: string;
	/**
	 * Boolean for determining whether the field value is not valid
	 */
	hasError?: boolean;
	/**
	 * Number for determining the text limit input
	 */
	maxLength?: number;
	/**
	 * Function to invoke on typing
	 */
	searchFunction: (searchTerm: string) => Promise<string[]>;
	/**
	 * onChange function for keeping track field value
	 * debounce is built in
	 */
	handleChange?: (selected: string) => void;
	/**
	 * Delay for invoking searchFuction after typing
	 */
	searchDelay?: number;
	/**
	 * default value for edit mode
	 */
	defaultValue?: string;
	/**
	 * Boolean for determining whether this is a disabled field
	 */
	disabled?: boolean;
	/** Element identifier selector for test */
	testId?: string;
}

/**
 * Search field let users enter a text and select any of the found results
 * @param {SearchFieldProps} Prop
 * @returns  an styled  input element
 */
export const SearchField = ({
	customStyles,
	label = '',
	placeholder = 'Please input a new value',
	errorMsg = 'Please fill up the field',
	hasError,
	maxLength,
	testId,
	searchFunction,
	handleChange = () => {
		return;
	},
	searchDelay = 200,
	defaultValue,
	disabled = false,
}: SearchFieldProps) => {
	const [value, setValue] = useState(defaultValue);
	const [opened, setOpened] = useState<boolean>(false);
	const [options, setOptions] = useState<string[]>([]);
	const [selected, setSelected] = useState<string>();
	const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

	useEffect(() => {
		setValue(defaultValue);
	}, [defaultValue]);

	useEffect(() => {
		const closeSelect = () => {
			setOpened(false);
		};
		document.addEventListener('click', closeSelect);

		return () => {
			document.removeEventListener('click', closeSelect);
		};
	}, []);

	const fetchOptions = async (searchTerm: string | undefined) => {
		if (searchTerm) {
			const results = await searchFunction(searchTerm);
			setOptions(results);
			setOpened(true);
		} else {
			setOptions([]);
		}
	};

	const delayedFetch = async (searchTerm: string | undefined) => {
		if (timeoutId) {
			clearTimeout(timeoutId);
		}

		const nextTimeoutId = setTimeout(() => {
			fetchOptions(searchTerm);
		}, searchDelay);
		setTimeoutId(nextTimeoutId);
	};

	const onType = (event: React.ChangeEvent<HTMLInputElement>) => {
		const nextValue = event.target.value;
		setValue(nextValue);
		delayedFetch(nextValue);
	};

	const onSelectOption = (option: string) => {
		setSelected(option);
		setValue(option);
		setOptions([]);
		handleChange(option);
	};

	return (
		<div className={[styles.container, customStyles].join(' ')}>
			{label && (
				<label htmlFor={label}>
					<p>{label}</p>
				</label>
			)}
			<input
				name={label}
				className={[
					styles.textField,
					hasError ? styles[`textField-error`] : undefined,
					styles[`textField-disabled-${disabled}`],
				].join(' ')}
				type="text"
				placeholder={placeholder}
				value={value}
				onChange={onType}
				maxLength={maxLength ? maxLength : undefined}
				disabled={disabled}
				data-testid={testId}
			/>
			{opened && options.length > 0 && (
				<div className={styles['dropdown-list']} data-testid={testId + 'Dropdown'}>
					{options.map((option, index) => (
						<div
							className={[
								styles['dropdown-list-item'],
								option === selected && styles['dropdown-list-item-selected'],
							].join(' ')}
							onClick={() => onSelectOption(option)}
							role="option"
							aria-selected={option === selected}
							key={index}
						>
							{option}
						</div>
					))}
				</div>
			)}
			<p className={[styles['error-msg'], !hasError && styles['error-msg-hide']].join(' ')}>
				{errorMsg}
			</p>
		</div>
	);
};
